import { Store } from '@ngrx/store';
import moment from 'moment';
import { ReplaySubject, takeUntil } from 'rxjs';

import { AppState, Timeout } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { closeRtcPeer } from 'app/utils/media/rtc';
import {
  IncomingMessage,
  IncomingMessageType,
  OutgoingMessageType,
  PeerCandidate,
  StreamingAnswerMessage,
  StreamingCandidateMessage,
  StreamingSubscribeMessage,
  StreamingUnsubscribeMessage,
} from 'messages/websocket.messages';

import ParticipantRtcStats from '../../preciate-stream/participant-rtc-stats';
import { socialAppActions } from '../../state/app';
import { SubscriptionState } from '../../types/webrtc';
import { RtcStatsStreamService, StatsCounter } from '../rtc-stats-stream';
import { SubscriptionReason, SubscriptionType } from '../subscription/subscription.types';
import { VideoRoomService } from '../video-room.service';
import { WebsocketService } from '../websocket.service';

const MIX_SUBSCRIPTION_ID = 'mix';

export class MixSubscriber extends Loggable {
  private _peer?: RTCPeerConnection;
  private peerTime = 0;
  private peerConnectionTime?: number; // Track peer connection start time (unix milliseconds)
  private _subscriptionState: SubscriptionState = SubscriptionState.NOT_SUBSCRIBED;
  private rtcStats?: ParticipantRtcStats;
  private rtcVideoStatsInterval?: Timeout;
  private retryTimeout?: Timeout;
  private destroyed$ = new ReplaySubject<boolean>(1);

  private audioStream?: MediaStream;
  private audioElement: HTMLAudioElement;
  private isShuttingDown = false;

  public constructor(
    private videoRoomService: VideoRoomService,
    private websocketService: WebsocketService,
    private rtcStatsStreamService: RtcStatsStreamService,
    private store: Store<AppState>,
    mixVolume: number,
    mixStream?: MediaStream,
  ) {
    super();
    this.setDisplayName('MixSubscriber');

    this.audioElement = document.createElement('audio');
    this.audioElement.volume = mixVolume / 100;
    this.audioElement.oncanplay = () => this.audioElement.play();

    if (mixStream) {
      this.audioElement.srcObject = mixStream;
    } else {
      this.websocketService.message$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleMessage);
      this.subscribe();
    }
  }

  public muteAudio(): void {
    if (this.audioElement) {
      this.audioElement.muted = true;
    }
  }

  public unmuteAudio(): void {
    if (this.audioElement) {
      this.audioElement.muted = false;
    }
  }

  /**
   *
   * @param volume Set the audio element volume
   */
  public setVolume(volume: number): void {
    this.audioElement.volume = volume / 100;
  }

  private subscribe() {
    this.info('Starting mix subscription', { peerTime: this.peerTime });

    const config = this.videoRoomService.createRtcConfig();
    const peer = new RTCPeerConnection(config);

    const peerTime = Date.now();
    this.peerTime = peerTime;

    peer.onconnectionstatechange = async () => {
      const { connectionState } = peer;
      this.info('Mix Subscribe state change', { connectionState });
      switch (connectionState) {
        case 'connected':
          this.subscriptionState = SubscriptionState.SUBSCRIBED;

          this.peerConnectionTime = Date.now();

          await this.sendRtcStats(connectionState).catch((error) => this.error('Error sendRTCStats', error));
          this.startRtcStats();
          break;

        case 'failed':
          // Only log an error and attempt recovery if we're not in the process of shutting down
          if (this.isShuttingDown) {
            this.info('Mix Peer connection failed during shutdown', { connectionState });
          } else {
            this.error('Mix Peer connection failed', { connectionState });
            this.subscriptionState = SubscriptionState.FAILED;
            await this.handleConnectionFailed();
          }
          break;

        default:
          break;
      }
    };

    peer.oniceconnectionstatechange = (): void => {
      const { iceConnectionState } = peer;
      this.info('Mix Subscribe ICE state change', { iceConnectionState, peerTime });
    };

    peer.onicegatheringstatechange = (): void => {
      const { iceGatheringState } = peer;
      this.info('Mix Subscribe ICE gathering state change', {
        iceGatheringState,
        peerTime,
      });
    };

    peer.onicecandidateerror = (e) => this.trace('Mix Candidate error', e);

    this.peer = peer;
    this.peer.ontrack = this.onTrack;
    this.peer.onicecandidate = (event): void => {
      this.sendCandidate(event, peerTime);
    };
    this.info('Mix Subscribe', { peerTime });

    const msg: StreamingSubscribeMessage = { t: OutgoingMessageType.STR_SUB, pt: peerTime };
    this.websocketService.sendMessage(msg);
  }

  private handleMessage = async (msg: IncomingMessage): Promise<void> => {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.STR_OFFER: {
        const { d: offer } = msg;
        await this.addOffer(offer);
        break;
      }
    }
  };

  public async addOffer(offer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peer || this.peerTime === 0) {
      this.warn('Mix Already unsubscribed');
      return;
    }

    await this.peer.setRemoteDescription(offer);
    this.peer.getTransceivers().forEach((t) => (t.direction = 'recvonly'));
    const answer = await this.peer.createAnswer();
    const { sdp, type } = answer;
    if (!type || !sdp) {
      this.error('addOffer: Got unexpected undefined value in answer', answer);
      return;
    }

    await this.peer.setLocalDescription(answer);

    const msg: StreamingAnswerMessage = {
      t: OutgoingMessageType.STR_ANSWER,
      answer: { type, sdp },
      pt: this.peerTime,
    };

    this.websocketService.sendMessage(msg);
  }

  private async sendRtcStats(state: string): Promise<void> {
    if (!this.peer) {
      this.warn('Mix No peer to get stats', { state });
      return;
    }
    const stats = await this.peer?.getStats();
    if (stats) {
      const rtcReports: RTCStatsReport[] = [];
      stats.forEach((report) => rtcReports.push(report));
      this.info('Mix RTC Stats ', { rtcReports, state });
    } else {
      this.info('Mix No RTC stats', { state });
    }
  }

  /**
   * RTC Stats
   */
  private startRtcStats(): void {
    this.info('RTC Stats START for mix');
    const rtcStats = (this.rtcStats = new ParticipantRtcStats(''));
    if (this.rtcVideoStatsInterval) {
      clearInterval(this.rtcVideoStatsInterval);
    }
    this.rtcVideoStatsInterval = setInterval(async () => {
      // Validate if peer and videoTrack is still available
      let isAudioStuck = false;

      if (!this.peer || this.peer.connectionState !== 'connected') {
        return;
      }

      const stats = await this.peer.getStats();
      isAudioStuck = rtcStats.isAudioStuck(stats, 'mix', 'mix');

      if (isAudioStuck) {
        this.warn('Mix Media is Stuck RESUBSCRIBE', {
          isAudioStuck,
        });
        this.destroyRtcStats();
        await this.handleConnectionFailed();
      }
    }, 1000);
  }

  private destroyRtcStats(): void {
    if (this.rtcVideoStatsInterval) {
      clearInterval(this.rtcVideoStatsInterval);
    }
    if (this.rtcStats) {
      this.info('Mix RTC Stats STOP');
    }
    this.rtcStats?.destroy();
    this.rtcVideoStatsInterval = undefined;
    this.rtcStats = undefined;
  }

  private sendCandidate(event: RTCPeerConnectionIceEvent, peerTime: number): void {
    const { candidate } = event;
    if (!candidate) {
      this.info('ICE Candidate was null, done');
      return;
    }

    if (!candidate.sdpMid) {
      this.error('Candidate sdpMid is unexpectedly null', candidate);
      return;
    }

    this.info('ICE Candidate received', candidate.toJSON());
    const msg: StreamingCandidateMessage = {
      t: OutgoingMessageType.STR_CANDIDATE,
      d: candidate as PeerCandidate,
      pt: peerTime,
    };
    this.websocketService.sendMessage(msg);
  }

  private onTrack = (event: RTCTrackEvent): void => {
    if (event.track.kind === 'audio') {
      this.info('Mix Audio track received');
      this.audioStream = event.streams[0];
      this.audioElement.srcObject = this.audioStream;
    }
  };

  private async handleConnectionFailed(): Promise<void> {
    this.rtcStatsStreamService.incrementCounter(StatsCounter.MIX_SUBSCRIBER_CONNECTION_FAILED);

    const oneMinuteAgo = moment().subtract(1, 'minute');
    const connectionTime = this.peerConnectionTime;

    // If the connection fails in under a minute, or we never connected, advance the reconnection state machine to
    // potentially more reliable methods
    if (!connectionTime || connectionTime > oneMinuteAgo.valueOf()) {
      this.videoRoomService.advanceReconnectionState({ connectionTime });
      // Clear the current connection time to avoid advancing state twice
      this.peerConnectionTime = undefined;
    }

    await this.sendRtcStats('failed');
    this.retrySubscription();
  }

  private retrySubscription(): void {
    if (!this.peer) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = undefined;
      return;
    }
    this.subscriptionState = SubscriptionState.RESUBSCRIBE;
    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = undefined;
      this.unsubscribe(this.peerTime);
      this.subscribe();
    }, 1500);
  }

  private unsubscribe(peerTime: number | undefined): void {
    if (this.peer && peerTime && this.subscriptionState !== SubscriptionState.UNSUBSCRIBING) {
      this.subscriptionState = SubscriptionState.UNSUBSCRIBING;
      this.info('Mix Unsubscribe', { peerTime: peerTime });
      this.peer = undefined;

      const msg: StreamingUnsubscribeMessage = {
        t: OutgoingMessageType.STR_UNSUB,
        pt: peerTime,
      };
      this.peerTime = 0;
      this.websocketService.sendMessage(msg);

      // Remove RTC Stats Interval
      this.destroyRtcStats();
    }
  }

  public destroy(): void {
    this.info('Destroying mix');
    this.isShuttingDown = true;

    this.store.dispatch(socialAppActions.REMOVE_SUBSCRIPTION({ subscriptionId: MIX_SUBSCRIPTION_ID }));

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    this.retryTimeout = undefined;
    // Unsubscribe from the mix
    this.unsubscribe(this.peerTime);

    this.audioElement.srcObject = null;

    this.destroyed$.next(true);
    this.destroyed$.complete();

    closeRtcPeer(this.peer);
    this.peer = undefined;
    this.peerTime = 0;
  }

  private set subscriptionState(state: SubscriptionState) {
    this.info(`Mix Subscription state ${this._subscriptionState} -> ${state}`);
    this._subscriptionState = state;
    this.store.dispatch(
      socialAppActions.CHANGE_SUBSCRIPTION_STATUS({
        status: {
          name: MIX_SUBSCRIPTION_ID,
          id: MIX_SUBSCRIPTION_ID,
          state,
          videoNeeded: false,
          audioNeeded: true,
          reason: SubscriptionReason.MIX,
          type: SubscriptionType.MIX,
          peerTime: 0,
        },
      }),
    );
  }

  public get peer(): RTCPeerConnection | undefined {
    return this._peer;
  }

  private set peer(newPeer: RTCPeerConnection | undefined) {
    if (newPeer) {
      this.rtcStatsStreamService.addSubscriptionMonitoring(MIX_SUBSCRIPTION_ID, SubscriptionType.MIX, newPeer);
    } else {
      this.rtcStatsStreamService.removeSubscriptionMonitoring(MIX_SUBSCRIPTION_ID);
    }
    this._peer = newPeer;
  }

  // #region Test Utilities
  public forceClosePeer(): void {
    if (!this.peer) {
      this.warn('No peer to close');
      return;
    }
    this.subscriptionState = SubscriptionState.FAILED;
    closeRtcPeer(this.peer);
  }
  // #endregion
}
